<template>
  <section class="giant" @click="open">
    <p class="giant__eyebrow">The Giant · {{ row.season }}</p>
    <h3 class="giant__title">{{ row.title }}</h3>
    <p class="giant__status">{{ row.size }}×{{ row.size }} · {{ (row.words || 0).toLocaleString() }} clues · {{ status }}</p>
    <div class="giant__actions">
      <button class="primary" @click.stop="open">{{ label }}</button>
      <GiantGroupButton v-for="g in groupIds" :key="g" :group-id="g" :giant-id="id" />
      <MyRoomButtons :date="id" />
    </div>
  </section>
</template>

<script>
// A seasonal Giant on the home screen (giant.mjs; Matt, 2026-10-19): open
// it alone, or in one of my groups' rooms - one room per group per Giant,
// kept for the whole season - or back into a room I share with a friend
// (MyRoomButtons). The status line is how much of it I've filled alone; each
// room's button carries its own (Sky, 2026-10-30).
import { mapState } from 'vuex';
import MyRoomButtons from '@/components/home/MyRoomButtons.vue';
import GiantGroupButton from '@/components/home/GiantGroupButton.vue';
import { formatTime } from '@/engine/solving';

export default {
  name: 'GiantCard',
  components: { MyRoomButtons, GiantGroupButton },
  props: {
    id: { type: String, required: true },
    row: { type: Object, required: true },
  },
  computed: {
    ...mapState(['progressByDate', 'groupIds']),
    progress() { return this.progressByDate[this.id] || null; },
    status() {
      const p = this.progress;
      if (p?.solved) return `solved ${p.together ? 'together' : 'alone'} in ${formatTime(p.seconds)}`;
      if (!p?.filled) return 'alone: not started';
      const pct = this.row.squares ? Math.max(1, Math.floor((p.filled / this.row.squares) * 100)) : null;
      return pct ? `alone: ${pct}% filled · ${formatTime(p.seconds)}` : `alone: under way · ${formatTime(p.seconds)}`;
    },
    label() {
      if (this.progress?.solved) return 'Look again';
      return this.progress?.filled ? 'Resume alone' : 'Start alone';
    },
  },
  methods: {
    open() { this.$store.dispatch('openPuzzle', this.id); },
  },
};
</script>

<style lang="scss" scoped>
.giant {
  border: 1px solid var(--accent-edge); border-radius: var(--radius); padding: 0.9rem 1rem; cursor: pointer;
  background:
    repeating-linear-gradient(0deg, transparent 0 11px, rgba(255, 255, 255, 0.035) 11px 12px),
    repeating-linear-gradient(90deg, transparent 0 11px, rgba(255, 255, 255, 0.035) 11px 12px),
    var(--surface);
}
.giant__eyebrow { margin: 0; font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); font-weight: 800; }
.giant__title { margin: 0.1rem 0 0.2rem; font-size: 1.3rem; }
.giant__status { margin: 0; color: var(--ink-dim); font-size: 0.85rem; font-variant-numeric: tabular-nums; }
.giant__actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.6rem;
  .primary, :deep(.secondary) { flex: 1 1 8rem; width: auto; } }
</style>
